import { useCallback } from 'react'

import { Ok, Result } from 'ts-results'

import { DataError, useApi } from 'app/common/application'

import {
  InviteUserGetRequest,
  InviteUserGetResponse,
  InviteUserRequest,
  InviteUserResponse,
  InviteUserStatus,
  WorkspaceActivity,
  WorkspaceCreateContent,
  WorkspaceCreateRequest,
  WorkspaceCreateResponse,
  WorkspaceDeliveries,
  WorkspaceUser,
} from '../types'

const useWorkspace = () => {
  const { create, read } = useApi()

  const createWorkspace = useCallback(
    (content: WorkspaceCreateContent): Promise<Result<string, DataError<number>>> =>
      create<WorkspaceCreateRequest, WorkspaceCreateResponse>('/core/user/create', {
        content,
      }).then((result) => (result.ok ? Ok(result.val.data.selectedWorkspaceId) : result)),
    [create],
  )

  const getWorkspaceUsers = useCallback(
    (): Promise<Result<WorkspaceUser[], DataError<number>>> =>
      read<WorkspaceUser[]>('/client/v1/workspace/users').then((result) =>
        result.ok ? Ok(result.val) : result,
      ),
    [read],
  )

  const getLastActivities = useCallback(
    () => read<WorkspaceActivity[]>('/client/v1/activities'),
    [read],
  )

  const getDeliveries = useCallback(() => read<WorkspaceDeliveries>('/client/v1/uploads'), [read])

  const getInvite = useCallback(
    (
      email: string,
      inviteCode: string,
      workspace: boolean,
    ): Promise<Result<InviteUserStatus, DataError<number>>> =>
      create<InviteUserGetRequest, InviteUserGetResponse>('/core/user/getInvite', {
        content: { email, inviteCode, workspace },
      }).then((result) => (result.ok ? Ok(result.val.data) : result)),
    [create],
  )

  const inviteUser = useCallback(
    (email: string): Promise<Result<void, DataError<number>>> =>
      create<InviteUserRequest, InviteUserResponse>('/core/user/inviteUser', {
        content: { email },
      }).then((result) => (result.ok ? Ok(undefined) : result)),
    [create],
  )

  return {
    createWorkspace,
    getWorkspaceUsers,
    getLastActivities,
    getDeliveries,
    getInvite,
    inviteUser,
  }
}

export default useWorkspace
